import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './components/auth/AuthProvider';
import { ToastProvider } from './components/ui/Toast';
import StudioPage from './pages/Studio';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import CookieConsent from './components/CookieConsent';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import { getDomainType, DOMAIN_TYPES } from './utils/domain';
// Pages légales
import MentionsLegales from './pages/legal/MentionsLegales';
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import Cookies from './pages/legal/Cookies';
// Pages contact
import Contact from './pages/Contact';
import About from './pages/About';
// Pages blog
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
// SEO
import HomePage from './pages/HomePage';
// ========== WAITLIST BETA (TEMPORAIRE - à supprimer après beta) ==========
import Waitlist from './pages/Waitlist';
import BetaSignup from './pages/BetaSignup';
import AdminWaitlist from './pages/admin/AdminWaitlist';
// ======================================================================
// PHOENIX UPDATE MANAGER - Reload automatique invisible (pas de notification)
import './services/updateManager'; // Auto-init au chargement

// RESILIENCE UTILITIES - Exposer dans la console pour debug
import { forceCleanSupabaseStorage, diagnoseSession } from './lib/supabase-resilience';
import { debugLog } from './utils/debug-logger';
import logger from './utils/logger';

// Exposer dans window pour debug en production
if (typeof window !== 'undefined') {
 (window as any).clairefyDebug = {
 forceCleanSupabase: forceCleanSupabaseStorage,
 diagnoseSession: diagnoseSession,
 help: () => {
 debugLog(`
CLAIREFY DEBUG UTILITIES

Commandes disponibles:
 clairefyDebug.diagnoseSession() - Diagnostic complet de la session
 clairefyDebug.forceCleanSupabase() - Nettoyer localStorage Supabase (si bloqué)
 clairefyDebug.help() - Afficher cette aide

Exemple d'utilisation:
 > clairefyDebug.diagnoseSession()
 {
 hasLocalStorage: true,
 isValid: false,
 hasCache: true,
 cacheAge: 12345,
 expiresIn: -7200,
 error: "Session is invalid/expired"
 }

 Si isValid = false → Exécuter: clairefyDebug.forceCleanSupabase()
 `);
 },
 };
 
 logger.log('[Debug] Resilience utilities exposed: window.clairefyDebug');
}

// Force rebuild to update Netlify configuration

function App() {
 logger.log('[App] Component rendering...');
 
 useEffect(() => {
 logger.log('[App] Component mounted', {
 url: window.location.href,
 pathname: window.location.pathname,
 hostname: window.location.hostname,
 userAgent: navigator.userAgent,
 localStorage_keys: Object.keys(localStorage),
 sessionStorage_keys: Object.keys(sessionStorage),
 });

 // Vérifier si on a des données corrompues dans localStorage
 try {
 const phoenixVersion = localStorage.getItem('phoenix_version');
 const supabaseAuth = localStorage.getItem('sb-clairefy-auth');
 
 logger.log('[App] localStorage state:', {
 hasPhoenixVersion: !!phoenixVersion,
 hasSupabaseAuth: !!supabaseAuth,
 phoenixVersion: phoenixVersion ? JSON.parse(phoenixVersion) : null,
 });
 } catch (error) {
 logger.error('[App] Error reading localStorage:', error);
 }
 }, []);
 
 return (
   <HelmetProvider>
     <ToastProvider>
       <AuthProvider>
         <Router>
           <DomainRouter />
         </Router>
       </AuthProvider>
     </ToastProvider>
   </HelmetProvider>
 );
}

// Composant qui gère le routing avec séparation stricte des domaines
function DomainRouter() {
 const location = useLocation();
 const domainType = getDomainType();
 const isLocal = domainType === DOMAIN_TYPES.LOCAL;

 logger.log('[DomainRouter] Render', {
 pathname: location.pathname,
 domainType,
 hostname: typeof window !== 'undefined' ? window.location.hostname : 'SSR',
 search: location.search,
 hash: location.hash,
 state: location.state,
 });

 // PLUS de redirections - TOUT est sur clairify.fr
 useEffect(() => {
 logger.log('[DomainRouter] useEffect - Route changed', {
 pathname: location.pathname,
 domainType,
 hostname: window.location.hostname,
 timestamp: new Date().toISOString(),
 });

 // Vérifier si on est sur la page d'accueil
 if (location.pathname === '/') {
 logger.log('[DomainRouter] On HomePage route, rendering HomePage component...');
 }
 }, [location.pathname, domainType]);

 useEffect(() => {
 logger.log('[DomainRouter] Component mounted', {
 initialPath: location.pathname,
 domainType,
 });

 return () => {
 logger.log('[DomainRouter] Component unmounting');
 };
 }, []);

 logger.log('[DomainRouter] Rendering Routes...');

 return (
 <>
 {/* PHOENIX: Reload automatique invisible, pas de notification */}
 
 <Routes>
 {/* TOUTES LES ROUTES disponibles sur tous les domaines - routing unifié */}
 <Route path="/" element={<HomePage />} />
 <Route path="/pricing" element={<Pricing />} />
 <Route path="/login" element={<Login />} />
 <Route path="/legal" element={<MentionsLegales />} />
 <Route path="/privacy" element={<Privacy />} />
 <Route path="/terms" element={<Terms />} />
 <Route path="/cookies" element={<Cookies />} />
 <Route path="/contact" element={<Contact />} />
 <Route path="/about" element={<About />} />
 <Route path="/blog" element={<Blog />} />
 <Route path="/blog/:slug" element={<BlogPost />} />
 <Route path="/studio" element={<StudioPage />} />
 <Route path="/settings" element={<Settings />} />
 <Route path="/billing" element={<Billing />} />
 
 {/* ========== ROUTES WAITLIST BETA (TEMPORAIRE) ========== */}
 <Route path="/waitlist" element={<Waitlist />} />
 <Route path="/beta-signup" element={<BetaSignup />} />
 <Route path="/admin/waitlist" element={<AdminWaitlist />} />
 {/* ======================================================== */}
 </Routes>
 
 <CookieConsent />
 </>
 );
}


export default App;
